import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAppContext } from '../components/Layout';
import Spinner from '../components/Spinner';
import { useAuth } from '../auth/AuthProvider';
import {
  useEventTemplates,
  useVisibleGoals,
  useVisibleInitiatives,
  useVisiblePriorities,
  useVisibleTasks,
  useVisibleContacts,
  useAssignablePeople,
  usePersonLabels,
} from '../hooks/useScopedData';
import {
  canCreateTaskForGoal,
  canEditTaskFully,
  canUpdateTaskStatus,
  initiativeCommittees,
  isAdmin,
} from '../lib/visibility';
import StatusBadge, { taskIsOverdue } from '../components/StatusBadge';
import TaskForm, { type TaskDraft } from '../components/TaskForm';
import { assigneePersonsOf, contactToPersonFrom } from '../lib/personRefs';
import TaskNotes from '../components/TaskNotes';
import RefBadge from '../components/RefBadge';
import GoalForm, { type GoalDraft } from '../components/GoalForm';
import InitiativeForm, { type InitiativeDraft } from '../components/InitiativeForm';
import { addTaskNote, createGoal, createTask, updateDocFields, updateTask } from '../lib/db';
import { createInitiativesFromDraft } from '../lib/initiatives';
import { recName, statusIsComplete, type Goal, type Initiative, type Task } from '../types';

export default function GoalsTasks() {
  const { profile, settings, districtFilter, districts } = useAppContext();
  const { firebaseUser } = useAuth();
  const email = firebaseUser?.email?.toLowerCase() ?? 'unknown';
  const navigate = useNavigate();

  const priorities = useVisiblePriorities();
  const goalsQ = useVisibleGoals(profile);
  const tasksQ = useVisibleTasks(profile);
  const contactsQ = useVisibleContacts(profile);
  const peopleQ = useAssignablePeople(profile);
  const toPerson = useMemo(() => contactToPersonFrom(contactsQ.data), [contactsQ.data]);
  const labelFor = usePersonLabels(tasksQ.data.flatMap((t) => assigneePersonsOf(t, toPerson)), peopleQ.byId);
  const initiativesQ = useVisibleInitiatives(profile);
  const templatesQ = useEventTemplates();

  const admin = isAdmin(profile);
  const activeTemplates = useMemo(() => templatesQ.data.filter((t) => t.active), [templatesQ.data]);
  const pickableCommittees = admin
    ? settings.committees
    : initiativeCommittees(profile, settings.committees);

  const [goalEdit, setGoalEdit] = useState<Goal | 'new' | null>(null);
  const [taskEdit, setTaskEdit] = useState<{ goal: Goal; task: Task | null } | null>(null);
  const [initiativeGoal, setInitiativeGoal] = useState<Goal | null>(null);

  // Deep link from a task-assignment notification email (?task=<id>) — open
  // it once data has loaded, then drop the param so re-navigating here
  // doesn't keep reopening it after the user closes the drawer.
  const [searchParams, setSearchParams] = useSearchParams();
  const openedFromLink = useRef(false);
  useEffect(() => {
    const wantedId = searchParams.get('task');
    if (!wantedId || openedFromLink.current || goalsQ.loading || tasksQ.loading) return;
    openedFromLink.current = true;
    const task = tasksQ.data.find((t) => t.id === wantedId);
    const goal = task ? goalsQ.data.find((g) => g.id === task.goalId) : null;
    if (task && goal) setTaskEdit({ goal, task });
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('task');
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, goalsQ.loading, tasksQ.loading, goalsQ.data, tasksQ.data]);
  const [notesFor, setNotesFor] = useState<Set<string>>(new Set());
  const toggleNotes = (id: string) =>
    setNotesFor((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const goals = useMemo(() => {
    const rank: Record<string, number> = { active: 0, completed: 1, archived: 2 };
    let list = [...goalsQ.data];
    if (districtFilter !== 'All') {
      list = list.filter((g) => (g.districtIds ?? []).includes(districtFilter));
    }
    // Active first, then completed, then archived — alphabetical within each.
    return list.sort(
      (a, b) =>
        (rank[a.status] ?? 1) - (rank[b.status] ?? 1) || a.title.localeCompare(b.title),
    );
  }, [goalsQ.data, districtFilter]);

  const inactiveCount = goals.filter((g) => g.status !== 'active').length;

  const tasksByGoal = useMemo(() => {
    const map = new Map<string, Task[]>();
    for (const t of tasksQ.data) {
      if (!t.goalId) continue; // initiative / standalone tasks live elsewhere
      if (districtFilter !== 'All' && t.districtId !== districtFilter) continue;
      const arr = map.get(t.goalId) ?? [];
      arr.push(t);
      map.set(t.goalId, arr);
    }
    for (const arr of map.values()) {
      arr.sort((a, b) => (a.dueDate ?? '9999').localeCompare(b.dueDate ?? '9999'));
    }
    return map;
  }, [tasksQ.data, districtFilter]);

  const initiativesByGoal = useMemo(() => {
    const map = new Map<string, Initiative[]>();
    for (const init of initiativesQ.data) {
      if (!init.strategicGoalId) continue;
      if (districtFilter !== 'All' && init.districtId !== districtFilter) continue;
      const arr = map.get(init.strategicGoalId) ?? [];
      arr.push(init);
      map.set(init.strategicGoalId, arr);
    }
    for (const arr of map.values()) {
      arr.sort((a, b) => (a.eventDate ?? '9999').localeCompare(b.eventDate ?? '9999'));
    }
    return map;
  }, [initiativesQ.data, districtFilter]);

  const priorityTitle = (id: string) =>
    priorities.data.find((p) => p.id === id)?.title ?? 'Unlinked Strategic Area';

  return (
    <>
      <div className="card">
        <div className="row">
          <div>
            <h2>Goals &amp; Tasks</h2>
            <div className="subtitle">
              Goals are containers. Tasks are the district-level work beneath them.
            </div>
          </div>
          {admin && (
            <button className="btn primary" onClick={() => setGoalEdit('new')}>
              + New Goal
            </button>
          )}
        </div>
      </div>

      {(goalsQ.loading || tasksQ.loading) && <div className="empty"><Spinner label="Loading…" /></div>}
      {goalsQ.error && <div className="card err">{goalsQ.error.message}</div>}
      {!goalsQ.loading && goals.length === 0 && (
        <div className="card empty">No goals visible for this view.</div>
      )}

      {goals.map((goal, i) => {
        const tasks = tasksByGoal.get(goal.id) ?? [];
        const initiatives = initiativesByGoal.get(goal.id) ?? [];
        const isArchived = goal.status !== 'active';
        const showDivider = isArchived && (i === 0 || goals[i - 1].status === 'active');
        const canAddTask = !isArchived && canCreateTaskForGoal(profile, goal);
        return (
          <Fragment key={goal.id}>
            {showDivider && (
              <div className="section-divider">
                <span>Archived &amp; completed</span>
                <span className="section-divider-count">{inactiveCount}</span>
              </div>
            )}
            <div className={`card${isArchived ? ' is-archived' : ''}`}>
              <div className="row">
                <div>
                  <div className="org-section-head" style={{ margin: '0 0 2px' }}>
                    {priorityTitle(goal.strategicPriorityId)}
                  </div>
                  <div className="goal-title">
                    {goal.title} <RefBadge value={goal.ref} />
                  </div>
                  <small>
                    {recName(settings.committees, goal.committee)} Committee ·{' '}
                    {(goal.districtIds ?? [])
                      .map((id) => recName(settings.districts, id))
                      .join(', ')}
                  </small>
                  {goal.councilDescription && (
                    <div style={{ marginTop: 6 }}>
                      <b style={{ fontSize: 13 }}>Council</b>
                      <BulletList text={goal.councilDescription} />
                    </div>
                  )}
                  {goal.districtDescription && (
                    <div style={{ marginTop: 6 }}>
                      <b style={{ fontSize: 13 }}>District</b>
                      <BulletList text={goal.districtDescription} />
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <span className={`badge ${goal.status === 'active' ? '' : 'neutral'}`}>
                    {goal.status}
                  </span>
                  {admin && (
                    <>
                      <button className="btn" onClick={() => setGoalEdit(goal)}>
                        Edit
                      </button>
                      {isArchived ? (
                        <button
                          className="btn"
                          onClick={() => updateDocFields('goals', goal.id, { status: 'active' })}
                        >
                          Restore
                        </button>
                      ) : (
                        <button
                          className="btn"
                          onClick={() => updateDocFields('goals', goal.id, { status: 'archived' })}
                        >
                          Archive
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>

              <div className="tasks">
                <div className="row">
                  <b>Tasks</b>
                  {canAddTask && (
                    <button className="btn" onClick={() => setTaskEdit({ goal, task: null })}>
                      + Add Task
                    </button>
                  )}
                </div>
              {tasks.length === 0 && <div className="empty">No tasks yet.</div>}
              {tasks.map((task) => (
                <Fragment key={task.id}>
                <div className="task">
                  <div>
                    <b>{task.title}</b> <RefBadge value={task.ref} />
                    <br />
                    <button
                      className="btn"
                      style={{ padding: '1px 6px', fontSize: 11 }}
                      onClick={() => toggleNotes(task.id)}
                    >
                      Notes
                      {task.noteLog?.length ? ` (${task.noteLog.length})` : ''}
                    </button>
                  </div>
                  <div>{recName(settings.districts, task.districtId)}</div>
                  <div>
                    {task.assignedPosition ? (
                      <>
                        ◉ {task.assignedPosition}
                        <br />
                        <small>by position</small>
                      </>
                    ) : assigneePersonsOf(task, toPerson).length ? (
                      <>
                        ● {assigneePersonsOf(task, toPerson).map((id) => labelFor(id) ?? 'Unknown').join(', ')}
                        <br />
                        <small>direct</small>
                      </>
                    ) : (
                      <small>Unassigned</small>
                    )}
                  </div>
                  <div>
                    {task.dueDate ? fmtDate(task.dueDate) : '—'}
                    <br />
                    {canUpdateTaskStatus(profile, task) ? (
                      <select
                        value={task.status}
                        onChange={(e) => void updateTask(task.id, { status: e.target.value }, email)}
                      >
                        {settings.taskStatuses.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.name}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <StatusBadge
                        statusName={recName(settings.taskStatuses, task.status)}
                        isComplete={statusIsComplete(settings.taskStatuses, task.status)}
                        overdue={taskIsOverdue(
                          task,
                          statusIsComplete(settings.taskStatuses, task.status),
                        )}
                      />
                    )}
                    {canEditTaskFully(profile, task) && (
                      <>
                        {' '}
                        <button
                          className="btn"
                          style={{ padding: '2px 6px' }}
                          onClick={() => setTaskEdit({ goal, task })}
                        >
                          Edit
                        </button>
                      </>
                    )}
                  </div>
                </div>
                {notesFor.has(task.id) && (
                  <div style={{ padding: '4px 0 10px' }}>
                    <TaskNotes
                      notes={task.noteLog}
                      legacyNote={task.notes}
                      canAdd={canUpdateTaskStatus(profile, task)}
                      onAdd={(text) =>
                        addTaskNote(
                          task.id,
                          { by: email, at: new Date().toISOString(), text },
                          email,
                        )
                      }
                    />
                  </div>
                )}
                </Fragment>
                ))}
              </div>

              {(initiatives.length > 0 || canAddTask) && (
                <div className="tasks">
                  <div className="row">
                    <b>Initiatives</b>
                    {canAddTask && (
                      <button className="btn" onClick={() => setInitiativeGoal(goal)}>
                        + Add Initiative
                      </button>
                    )}
                  </div>
                  {initiatives.length === 0 && <div className="empty">No initiatives yet.</div>}
                  {initiatives.map((init) => (
                    <div key={init.id} className="task">
                      <div>
                        <b>{init.name}</b> <RefBadge value={init.ref} />
                      </div>
                      <div>{recName(settings.districts, init.districtId)}</div>
                      <div>{init.eventDate ? fmtDate(init.eventDate) : 'Date TBD'}</div>
                      <div>
                        <span className={`badge ${init.status === 'planning' || init.status === 'active' ? '' : 'neutral'}`}>
                          {init.status}
                        </span>{' '}
                        <button
                          className="btn"
                          style={{ padding: '2px 6px' }}
                          onClick={() => navigate('/initiatives', { state: { focusInitiativeId: init.id } })}
                        >
                          View
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Fragment>
        );
      })}

      {goalEdit && (
        <GoalForm
          initial={goalEdit === 'new' ? null : goalEdit}
          priorities={priorities.data.filter((p) => p.status === 'active')}
          settings={settings}
          onClose={() => setGoalEdit(null)}
          onSave={async (v: GoalDraft) => {
            if (goalEdit === 'new') {
              await createGoal({ ...v, status: 'active', createdBy: email });
            } else {
              await updateDocFields('goals', goalEdit.id, v);
            }
            setGoalEdit(null);
          }}
        />
      )}

      {taskEdit && (
        <TaskForm
          context={{
            title: taskEdit.goal.title,
            districtId: taskEdit.goal.districtIds[0] ?? '',
            committee: taskEdit.goal.committee,
          }}
          initial={taskEdit.task}
          settings={settings}
          people={peopleQ.options}
          labelFor={(id) => labelFor(id)}
          toPerson={toPerson}
          districts={admin ? settings.districts : districts}
          onClose={() => setTaskEdit(null)}
          onSave={async (draft: TaskDraft) => {
            const payload = draftToTask(draft);
            if (taskEdit.task) {
              await updateTask(taskEdit.task.id, payload, email);
            } else {
              await createTask({
                goalId: taskEdit.goal.id,
                initiativeId: null,
                source: 'goal',
                createdBy: email,
                dueDate: null,
                assignedPosition: null,
                assignedPersonIds: [],
                ...payload,
              });
            }
            setTaskEdit(null);
          }}
        />
      )}

      {initiativeGoal && (
        <InitiativeForm
          initial={null}
          presetDraft={{
            districtIds: initiativeGoal.districtIds,
            committee: initiativeGoal.committee,
            strategicGoalId: initiativeGoal.id,
          }}
          templates={activeTemplates}
          goals={goalsQ.data}
          settings={settings}
          districts={admin ? settings.districts : districts}
          committees={pickableCommittees}
          onClose={() => setInitiativeGoal(null)}
          onSave={async (draft: InitiativeDraft, template) => {
            await createInitiativesFromDraft(draft, template, settings, email);
            setInitiativeGoal(null);
          }}
        />
      )}
    </>
  );
}

function fmtDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** Each line the person typed becomes its own bullet — no markup to learn. */
function BulletList({ text }: { text: string }) {
  const lines = text.split('\n').map((l) => l.trim()).filter(Boolean);
  return (
    <ul className="goal-bullets">
      {lines.map((line, i) => (
        <li key={i}>{line}</li>
      ))}
    </ul>
  );
}

type TaskFields = Pick<
  Task,
  | 'title'
  | 'description'
  | 'districtId'
  | 'committee'
  | 'status'
  | 'dueDate'
  | 'assignedPosition'
  | 'assignedPersonIds'
>;

function draftToTask(draft: TaskDraft): TaskFields {
  return {
    title: draft.title.trim(),
    description: draft.description.trim(),
    districtId: draft.districtId,
    committee: draft.committee,
    status: draft.status,
    dueDate: draft.dueDate,
    assignedPosition: draft.assignmentMode === 'position' ? draft.assignedPosition : null,
    assignedPersonIds: draft.assignmentMode === 'people' ? draft.assignedPersonIds : [],
  };
}
